// components/Gacha/TitleGachaRoulette.jsx
import { useState, useEffect } from 'react';
import {
    Box, Button, Typography, CircularProgress, Alert, Paper,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';

const ITEM_HEIGHT = 72; // 슬롯 한 칸 높이

export default function TitleGachaRoulette() {
    const { userInfo, refreshUserInfo } = useUser();
    const points = userInfo?.points ?? 0;

    const [spinning, setSpinning] = useState(false);
    const [spinList, setSpinList] = useState([]);
    const [result, setResult] = useState(null);
    const [price, setPrice] = useState(10);
    const [enabled, setEnabled] = useState(true);
    const [error, setError] = useState('');
    const [tierClr, setTierClr] = useState({});

    useEffect(() => {
        api.get('/api/gacha/config/').then(r => {
            const cfg = r.data.configs?.find(c => c.type === 'title');
            if (cfg) setPrice(cfg.price);
            setEnabled(cfg.enabled);
        });

        api.get('/api/rewards/tier-probability/').then(r => {
            const map = {};
            (r.data.tiers || []).forEach(t => (map[t.tier] = t.color));
            setTierClr(map);
        });
    }, []);

    const colorOf = t => tierClr[t] || '#95a5a6';

    const draw = () => {
        setSpinning(true);
        setResult(null);
        setError('');
        setSpinList([]);

        api.post('/api/gacha/draw-spin/', { type: 'title' })
            .then(res => {
                setSpinList(res.data.spin_list || []);

                setTimeout(async () => {
                    setResult(res.data);

                    if (res.data.title && !res.data.already_owned) {
                        confetti({ particleCount: 150, spread: 70, origin: { y: 0.3 } });
                    }

                    await refreshUserInfo();
                    setSpinning(false);
                }, 2400);
            })
            .catch(err => {
                setError(err.response?.data?.message || '뽑기 실패');
                setSpinning(false);
            });
    };

    const ResultCard = () => {
        if (!result) return null;
        const { title, already_owned } = result;

        const bg = title ? colorOf(title.tier) : '#9e9e9e';
        const msg = title
            ? already_owned ? '🥲 이미 보유 중!' : '🎉 새로운 칭호 획득!'
            : '😵 꽝!';

        return (
            <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={
                    already_owned
                        ? { duration: 1.2, ease: 'easeOut' }
                        : { type: 'spring', stiffness: 450, damping: 18 }
                }
            >
                <Box
                    sx={{
                        height: ITEM_HEIGHT * 1.5,
                        bgcolor: bg,
                        color: '#fff',
                        borderRadius: 3,
                        pt: 3, pb: 4,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'flex-start', gap: 2,
                        animation: 'glow 1.2s ease-in-out 6 alternate',
                    }}
                >
                    <Typography variant="h6" fontWeight={400} sx={{ lineHeight: 1.2 }}>
                        {title?.name || '꽝'}
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                        {msg}
                    </Typography>
                </Box>
            </motion.div>
        );
    };

    return (
        <Box sx={{ maxWidth: 420, mx: 'auto', mt: 4 }}>
            <Typography variant="h5" gutterBottom>
                🎰 칭호 룰렛 뽑기
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
                가격: {price} Leaf🌿 / 보유: {points} Leaf🌿
            </Typography>

            <Button
                variant="contained"
                fullWidth
                onClick={draw}
                disabled={spinning || points < price || !enabled}
            >
                {spinning ? <CircularProgress size={24} /> : '룰렛 돌리기'}
            </Button>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            <Box
                component={Paper}
                elevation={3}
                sx={{
                    mt: 4, height: ITEM_HEIGHT * 1.5, overflow: 'hidden',
                    borderRadius: 3, position: 'relative',
                }}
            >
                <AnimatePresence>
                    {spinning && (
                        <motion.div
                            key="spin"
                            initial={{ y: 0 }}
                            animate={{ y: -(ITEM_HEIGHT * 1.5) * (spinList.length - 1) }}
                            transition={{ duration: 2.4, ease: 'circOut' }}
                        >
                            {spinList.map((it, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        height: ITEM_HEIGHT * 1.5,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 22, fontWeight: idx === spinList.length - 1 ? 700 : 400,
                                        color: '#fff', bgcolor: colorOf(it.tier),
                                    }}
                                >
                                    {it.name}
                                </Box>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {!spinning && <ResultCard />}
            </Box>
        </Box>
    );
}
